
import { useState, useEffect, useCallback } from 'react';
import { User } from '@/lib/types';
// IMPORTANT: Import directly from integration to prevent circular dependencies
import { supabase } from '@/integrations/supabase/client';
import { convertSupabaseUser } from './utils';
import { useToast } from '@/hooks/use-toast';

export const useAuthEvents = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Create a function to refresh products without causing circular imports
  const refreshProducts = useCallback(async () => {
    try {
      // Use dynamic import to avoid circular dependencies
      const { fetchProductsFromApi } = await import('@/lib/storage/product/api');
      await fetchProductsFromApi(true);
    } catch (e) {
      console.error('Failed to refresh products:', e);
    }
  }, []);

  useEffect(() => {
    const setupAuth = async () => {
      try {
        setIsLoading(true);
        
        // Check for existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting auth session:', error);
        }
        
        if (session?.user) {
          console.log(`Auth provider initialized with user: ${session.user.email} (${session.user.id})`);
          const convertedUser = convertSupabaseUser(session.user);
          setUser(convertedUser);
          console.log('User state set in AuthProvider:', convertedUser);
          
          // Force a refresh of products when authenticated
          await refreshProducts();
        } else {
          console.log('No active session found during AuthProvider initialization');
        }

        // Listen for auth changes
        const { data: { subscription } } = await supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log(`Auth state changed in listener: ${event}`);
            
            if (session?.user) {
              const convertedUser = convertSupabaseUser(session.user);
              console.log(`Setting user state: ${convertedUser.email} (${convertedUser.id})`);
              setUser(convertedUser);
              
              // Only force refresh products on SIGNED_IN events
              if (event === 'SIGNED_IN') {
                await refreshProducts();
              }
            } else {
              console.log('Clearing user state - no active session');
              setUser(null);
            }
          }
        );

        return () => {
          console.log('Unsubscribing from auth state changes');
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error setting up auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    setupAuth();
  }, [refreshProducts]);

  return { user, setUser, isLoading };
};
